/**
 * Connections — the Followers / Following / Buddies list behind a profile's
 * stats bar. Own profile adds the buddy-request filter and the per-row action
 * menu; another member's list is read-only, exactly like mobile.
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useProfile } from '@wlb/shared/context/ProfileContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import useDebounce from '@wlb/shared/hooks/useDebounce';
import useOtherUserProfile from '@wlb/shared/hooks/useOtherUserProfile';
import useUserConnections, {
  type BuddiesSubTab,
  type ConnectionAction,
  type ConnectionTab,
} from '@wlb/shared/hooks/useUserConnections';
import { ToastService } from '@wlb/shared/platform/notifier';
import type { UserConnectionItem } from '@wlb/shared/types/userProfile';
import { Button } from '../components/ui/Button';
import { Tabs } from '../components/ui/Tabs';
import { PopoverMenu } from '../components/ui/PopoverMenu';
import { ChevronLeftIcon, SearchIcon } from '../components/ui/icons';
import { ProfileRestricted } from '../features/profile/ProfileRestricted';
import { useOpenUserProfile } from '../features/profile/useOpenUserProfile';
import { ConnectionRow } from '../features/profile/connections/ConnectionRow';
import { ConnectionRowsSkeleton } from '../features/profile/connections/ConnectionRowsSkeleton';
import { ConnectionRequestModal } from '../features/profile/connections/ConnectionRequestModal';
import { buildConnectionsMenu, type ConnectionMenuKey } from '../features/profile/connections/connectionsMenu';
import {
  connectionAvatarUrl,
  connectionDisplayName,
  connectionProfileGuid,
} from '../features/profile/connections/connectionUser';
import { ConfirmModal } from '../features/feed/ConfirmModal';
import { BlockUserModal } from '../features/feed/BlockUserModal';
import { useOpenDirectConversation } from '../features/messages/useOpenDirectConversation';
import { getUsernameDisplay } from '../utils/usernameDisplay';
import '../components/ui/skeleton.css';
import '../features/profile/profile.css';
import '../features/profile/connections/connections.css';

const TABS: readonly ConnectionTab[] = ['followers', 'following', 'buddies'];
const SUB_TABS: readonly BuddiesSubTab[] = ['active', 'requested'];

type PendingAction = Exclude<ConnectionMenuKey, 'message'>;

interface PendingPrompt {
  user: UserConnectionItem;
  action: PendingAction;
}

const parseTab = (value: string | null): ConnectionTab =>
  TABS.find(tab => tab === value) ?? 'followers';

const parseSubTab = (value: string | null): BuddiesSubTab =>
  SUB_TABS.find(st => st === value) ?? 'active';

export function Connections() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userGuid } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
   const openUserProfile = useOpenUserProfile();
  const { openConversation } = useOpenDirectConversation();

  const isOwnProfile = !userGuid;
  const { profile: ownProfile, loading: ownLoading } = useProfile();
  const {
    profile: otherProfile,
    loading: otherLoading,
    error: otherError,
    restriction,
  } = useOtherUserProfile(isOwnProfile ? null : userGuid);

  const profile = isOwnProfile ? ownProfile : otherProfile;
  const profileLoading = isOwnProfile ? ownLoading : otherLoading;
  const profileUsername = getUsernameDisplay(profile);

  const tab = parseTab(searchParams.get('tab'));
  const subTab = parseSubTab(searchParams.get('subtab'));
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const [menu, setMenu] = useState<{ user: UserConnectionItem; anchorRect: DOMRect } | null>(null);
  const [prompt, setPrompt] = useState<PendingPrompt | null>(null);

  const connections = useUserConnections({
    profileUserId: profile?.userId,
    tab,
    subTab,
    isOwnProfile,
    searchText: debouncedSearch.trim(),
  });

  // The backend may ignore `searchText`, so the typed term is applied locally too.
  const visibleUsers = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    if (!term) return connections.users;
    return connections.users.filter(user =>
      (user.userName ?? '').toLowerCase().includes(term),
    );
  }, [connections.users, debouncedSearch]);

  const tabItems = useMemo(
    () => TABS.map(key => ({ key, label: t(`userConnections.${key}.title`) })),
    [t],
  );

  const selectTab = (next: ConnectionTab) => {
    setSearchParams({ tab: next }, { replace: true });
  };

  const selectSubTab = (next: BuddiesSubTab) => {
    setSearchParams({ tab: 'buddies', subtab: next }, { replace: true });
  };

    const isRequestedTab = isOwnProfile && tab === 'buddies' && subTab === 'requested';

  const handleInlineAccept = async (user: UserConnectionItem) => {
    const result = await connections.runAction(user, 'acceptRequest');
    if (!result.success) {
      ToastService.show({ type: 'error', message: result.message || t('connections.actionError') });
    } else {
      ToastService.show({ type: 'success', message: t('home.notifications.toasts.buddyAccepted') });
    }
  };

  const handleInlineReject = async (user: UserConnectionItem) => {
    const result = await connections.runAction(user, 'rejectRequest');
    if (!result.success) {
      ToastService.show({ type: 'error', message: result.message || t('connections.actionError') });
    } else {
      ToastService.show({ type: 'success', message: t('home.notifications.toasts.buddyRejected') });
    }
  };

  const handleMenuSelect = (key: ConnectionMenuKey) => {
    const target = menu?.user;
    setMenu(null);
    if (!target) return;

    if (key === 'message') {
      openConversation({
        userId: target.userId,
        name: connectionDisplayName(target),
        avatar: connectionAvatarUrl(target),
      }).catch(() => undefined);
      return;
    }
    setPrompt({ user: target, action: key });
  };

  const runPendingAction = async (action: ConnectionAction) => {
    if (!prompt) return;
    const result = await connections.runAction(prompt.user, action);
    setPrompt(null);
    if (!result.success) {
      ToastService.show({
        type: 'error',
        message: result.message || t('connections.actionError'),
      });
    }
  };

  const renderPrompt = () => {
    if (!prompt) return null;
    const name = connectionDisplayName(prompt.user);
    const avatarUrl = connectionAvatarUrl(prompt.user);

    if (prompt.action === 'block') {
      return (
        <BlockUserModal
          username={name}
          avatarUrl={avatarUrl || null}
          seed={connectionProfileGuid(prompt.user) ?? prompt.user.userId}
          loading={!!connections.actionLoading}
          onClose={() => setPrompt(null)}
          onConfirm={() => { runPendingAction('block').catch(() => undefined); }}
        />
      );
    }

    if (prompt.action === 'respondRequest') {
      const promptedUserId = prompt.user.userId;
      const acceptLoading =
        connections.actionLoading?.userId === promptedUserId &&
        connections.actionLoading?.action === 'acceptRequest';
      const rejectLoading =
        connections.actionLoading?.userId === promptedUserId &&
        connections.actionLoading?.action === 'rejectRequest';

      return (
        <ConnectionRequestModal
          username={name}
          avatarUrl={avatarUrl}
          seed={connectionProfileGuid(prompt.user) ?? prompt.user.userId}
          acceptLoading={acceptLoading}
          rejectLoading={rejectLoading}
          onClose={() => setPrompt(null)}
          onAccept={() => { runPendingAction('acceptRequest').catch(() => undefined); }}
          onReject={() => { runPendingAction('rejectRequest').catch(() => undefined); }}
        />
      );
    }

    const isUnfollow = prompt.action === 'unfollow';
    return (
      <ConfirmModal
        title={t(isUnfollow ? 'profile.unfollow.title' : 'connections.removeBuddy.title', { name })}
        message={t(
          isUnfollow ? 'profile.unfollow.description' : 'connections.removeBuddy.description',
          { name },
        )}
        confirmLabel={t(isUnfollow ? 'profile.unfollow.confirm' : 'connections.removeBuddy.confirm')}
        cancelLabel={t('common.buttons.cancel')}
        mediaUrl={avatarUrl || null}
        loading={!!connections.actionLoading}
        onClose={() => setPrompt(null)}
        onConfirm={() => {
          runPendingAction(isUnfollow ? 'unfollow' : 'removeBuddy').catch(() => undefined);
        }}
      />
    );
  };

  const renderList = () => {
    if (connections.loading && visibleUsers.length === 0) {
      return <ConnectionRowsSkeleton />;
    }

    if (connections.error && visibleUsers.length === 0) {
      return (
        <div className="connections-status connections-status--error" role="alert">
          <p>{connections.error || t('connections.loadError')}</p>
          <Button size="sm" onClick={connections.refresh}>{t('common.buttons.retry')}</Button>
        </div>
      );
    }

    if (visibleUsers.length === 0) {
      return (
        <div className="connections-empty">
          <p className="connections-empty__title">{t(`userConnections.${tab}.emptyTitle`)}</p>
          <p className="connections-empty__message">
            {debouncedSearch.trim()
              ? t('connections.emptySearch')
              : isOwnProfile
                ? t(`userConnections.${tab}.emptyDescription`)
                : t(`connections.othersEmptyDescription.${tab}`, { name: profileUsername })}
          </p>
        </div>
      );
    }
     return (
      <>
        {connections.loading && (
          <div className="connections-refresh-loader" aria-label={t('common.loading')} />
        )}
        <ul className="connections-list skeleton-fade-in">
          {visibleUsers.map(user => (
            <ConnectionRow
              key={user.userId}
              user={user}
              menuOpen={menu?.user.userId === user.userId}
              onOpenProfile={target => openUserProfile(connectionProfileGuid(target))}
              onOpenMenu={
                isOwnProfile && !isRequestedTab
                  ? (target, anchorRect) => setMenu({ user: target, anchorRect })
                  : undefined
              }
              onAccept={isRequestedTab ? handleInlineAccept : undefined}
              onReject={isRequestedTab ? handleInlineReject : undefined}
              actionLoading={connections.actionLoading}
            />
          ))}
        </ul>
        {connections.hasMore && (
          <div className="connections-more">
            <Button
              size="sm"
              variant="outline"
              onClick={connections.loadMore}
              disabled={connections.loadingMore}
            >
              {t(connections.loadingMore ? 'connections.loadingMore' : 'connections.loadMore')}
            </Button>
          </div>
        )}
      </>
    );
  };

  const renderBody = () => {
    if (!isOwnProfile && restriction) {
      return <ProfileRestricted reason={restriction} />;
    }

    if (profileLoading && !profile) {
      return <ConnectionRowsSkeleton />;
    }

    if (!profile) {
      return (
        <div className="connections-status connections-status--error" role="alert">
          <p>{otherError || t('profile.toast.unableToLoadProfile')}</p>
        </div>
      );
    }

    return (
      <>
        <div className="connections-search">
          <input
            type="search"
            className="connections-search__input"
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder={t(`connections.searchPlaceholder.${tab}`)}
            aria-label={t('connections.accessibility.search')}
          />
          <SearchIcon size={24} className="connections-search__icon" />
        </div>

        <Tabs
          items={tabItems}
          activeKey={tab}
          onChange={selectTab}
          label={t('connections.accessibility.tabs')}
          idPrefix="connections-tab"
        />

        {isOwnProfile && tab === 'buddies' && (
          <div className="connections-subtabs" role="group" aria-label={t('connections.accessibility.buddyFilters')}>
            {SUB_TABS.map(key => (
              <button
                key={key}
                type="button"
                aria-pressed={subTab === key}
                className={`connections-subtab${subTab === key ? ' connections-subtab--active' : ''}`}
                onClick={() => selectSubTab(key)}
              >
                {t(`userConnections.buddies.${key}`)}
              </button>
            ))}
          </div>
        )}

        <div
          role="tabpanel"
          id={`connections-tab-panel-${tab}`}
          aria-labelledby={`connections-tab-${tab}`}
        >
          {renderList()}
        </div>
      </>
    );
  };

  return (
    <section className="connections-page" aria-label={t('connections.title')}>
      <div className="profile-topbar">
        <button
          type="button"
          className="profile-topbar__back"
          aria-label={t('profile.accessibility.back')}
          onClick={() => navigate(-1)}
        >
          <ChevronLeftIcon size={20} />
        </button>
        <h1 className="profile-topbar__title">{profileUsername || t('connections.title')}</h1>
      </div>

      {renderBody()}

      {menu && (
        <PopoverMenu
          anchorRect={menu.anchorRect}
          items={buildConnectionsMenu(tab, menu.user, t)}
          label={t('connections.accessibility.userMenu')}
          labelledBy="connections-menu-title"
          onClose={() => setMenu(null)}
          onSelect={handleMenuSelect}
        />
      )}

      {renderPrompt()}
    </section>
  );
}
